import { useState, useEffect } from "react";
import axios from "axios";
import { DownloadSimple, TrendUp, Users, ChartBar, CalendarBlank } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useAuth, isAdmin } from "@/lib/access";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { toast } from "sonner";

const API = `${window.location.origin}/api`;
const CHART_TICK_SM = { fontSize: 11 };
const CHART_TICK = { fontSize: 12 };
const CHART_GRID_STROKE = "#f3f4f6";
const CHART_BAR_RADIUS = [4, 4, 0, 0];

export default function ReportsPage() {
  const [analytics, setAnalytics] = useState(null);
  const [teamPerf, setTeamPerf] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leadPeriod, setLeadPeriod] = useState("day");
  const [leadsByPeriod, setLeadsByPeriod] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [aRes, tRes] = await Promise.all([
          axios.get(`${API}/crm/analytics`),
          axios.get(`${API}/crm/team-performance`)
        ]);
        setAnalytics(aRes.data);
        setTeamPerf(tRes.data);
      } catch (err) { toast.error("Failed to load reports"); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchPeriodData = async () => {
      try {
        const res = await axios.get(`${API}/leads-analytics/by-period`, { params: { period: leadPeriod } });
        setLeadsByPeriod(res.data);
      } catch (err) { toast.error("Failed to load period data"); }
    };
    fetchPeriodData();
  }, [leadPeriod]);

  const handleExportLeads = () => window.open(`${API}/export/leads`, "_blank");
  const user = useAuth();
  const handleExportTeam = () => window.open(`${API}/export/team`, "_blank");

  if (loading) {
    return <div className="space-y-6">{[1, 2, 3].map((i) => <div key={i} className="h-48 bg-gray-100 rounded-xl animate-pulse" />)}</div>;
  }

  const a = analytics || { total_leads: 0, closed_won: 0, closed_lost: 0, conversion_rate: 0, by_status: [], by_member: [] };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-gray-900" style={{ fontFamily: "'Gupter', serif" }}>Reports & Analytics</h1>
          <p className="text-xs font-bold text-indigo-600">Performance overview and exports</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExportLeads} data-testid="export-leads-report">
            <DownloadSimple size={16} className="mr-1" /> Export Leads
          </Button>
          {isAdmin(user) && (
          <Button variant="outline" size="sm" onClick={handleExportTeam} data-testid="export-team-report">
            <DownloadSimple size={16} className="mr-1" /> Export Team
          </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total Leads", value: a.total_leads, color: "text-indigo-700", bg: "bg-indigo-50", border: "border-indigo-200" },
          { label: "Closed Won", value: a.closed_won, color: "text-green-700", bg: "bg-green-50", border: "border-green-200" },
          { label: "Closed Lost", value: a.closed_lost, color: "text-red-700", bg: "bg-red-50", border: "border-red-200" },
          { label: "Conversion Rate", value: `${a.conversion_rate}%`, color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200" },
        ].map((s) => (
          <Card key={s.label} className={`p-4 border-2 ${s.border} ${s.bg} hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer`} data-testid={`report-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
            <p className={`text-[11px] font-bold uppercase tracking-wide mb-1 ${s.color}`}>{s.label}</p>
            <p className={`text-2xl sm:text-3xl font-black ${s.color}`} style={{ fontFamily: "'JetBrains Mono', monospace" }}>{s.value}</p>
          </Card>
        ))}
      </div>

      {/* Leads by Assignee */}
      <Card className="p-5 border border-gray-200 bg-white">
        <h3 className="text-base font-black text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Gupter', serif" }}>
          <span className="w-1 h-5 bg-indigo-600 rounded-full"></span>
          <Users size={16} className="text-indigo-600" weight="bold" /> Leads by Team Member
        </h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={a.by_member}>
            <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
            <XAxis dataKey="name" tick={CHART_TICK_SM} />
            <YAxis tick={CHART_TICK} />
            <Tooltip />
            <Bar dataKey="count" fill="#6366f1" radius={CHART_BAR_RADIUS} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Team Performance Table */}
      <Card className="border border-gray-200 bg-white overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-base font-black text-gray-900 flex items-center gap-2" style={{ fontFamily: "'Gupter', serif" }}>
            <span className="w-1 h-5 bg-blue-600 rounded-full"></span>
            <ChartBar size={16} className="text-blue-600" weight="bold" /> Team Performance
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="team-perf-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {["Member", "Role", "Total Leads", "Won", "Lost", "Active", "Win Rate"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {teamPerf.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400 text-sm">No team data yet</td></tr>
              ) : teamPerf.map((m) => (
                <tr key={m.name} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">{m.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{m.role}</td>
                  <td className="px-4 py-3 text-sm font-mono">{m.total}</td>
                  <td className="px-4 py-3 text-sm font-mono text-green-600">{m.won}</td>
                  <td className="px-4 py-3 text-sm font-mono text-red-600">{m.lost}</td>
                  <td className="px-4 py-3 text-sm font-mono text-blue-600">{m.active}</td>
                  <td className="px-4 py-3 text-sm font-mono font-semibold">{m.total > 0 ? `${Math.round((m.won / m.total) * 100)}%` : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Lead Generation by Period */}
      <Card className="p-5 border border-gray-200 bg-white" data-testid="lead-generation-chart">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <CalendarBlank size={16} className="text-indigo-500" /> Lead Generation Over Time
          </h3>
          <Select value={leadPeriod} onValueChange={setLeadPeriod}>
            <SelectTrigger className="w-[130px] h-8" data-testid="lead-period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">By Day</SelectItem>
              <SelectItem value="week">By Week</SelectItem>
              <SelectItem value="month">By Month</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {leadsByPeriod.length === 0 ? (
          <p className="text-center text-gray-400 py-8 text-sm">No lead date data available. Add leads with a Lead Date to see this chart.</p>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={leadsByPeriod}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
              <XAxis dataKey="period" tick={CHART_TICK_SM} />
              <YAxis tick={CHART_TICK} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="total" name="Total Leads" fill="#6366f1" radius={CHART_BAR_RADIUS} />
              <Bar dataKey="won" name="Won" fill="#22c55e" radius={CHART_BAR_RADIUS} />
              <Bar dataKey="lost" name="Lost" fill="#ef4444" radius={CHART_BAR_RADIUS} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>
    </div>
  );
}
